import { useState, useEffect, useCallback } from 'react';

interface RecentlyViewedVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  price: number;
  image: string;
  mileage: number;
}

const STORAGE_KEY = 'rockhound_recently_viewed';
const MAX_ITEMS = 6;

export const useRecentlyViewed = () => {
  const [recentlyViewed, setRecentlyViewed] = useState<RecentlyViewedVehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setRecentlyViewed(JSON.parse(stored));
      }
    } catch {
      setRecentlyViewed([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ✅ Uses in-memory state — no redundant localStorage reads
  const addRecentlyViewed = useCallback((vehicle: RecentlyViewedVehicle) => {
    setRecentlyViewed(prev => {
      const filtered = prev.filter(item => item.id !== vehicle.id);
      const updated = [vehicle, ...filtered].slice(0, MAX_ITEMS);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch {
        // Silent — continue without persisting
      }
      return updated;
    });
  }, []);

  // ✅ Uses in-memory state — no redundant localStorage reads
  const isRecentlyViewed = useCallback(
    (vehicleId: string): boolean => recentlyViewed.some(item => item.id === vehicleId),
    [recentlyViewed]
  );

  return {
    recentlyViewed,
    isLoading,
    addRecentlyViewed,
    isRecentlyViewed,
  };
};
